import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Mail } from 'lucide-react'
import Button from '@/components/button'
import FormField from '@/components/form-field'
import { Input, PasswordInput } from '@/components/input'
import AuthLayout from '@/layouts/auth-layout'
import { AuthApi } from '@/services/api-routes'
import type { LoginAutenticado, LoginResponse } from '@/types/auth'
import { loginAutenticado } from '@/types/auth'
import { useAuth } from '@/utility/context/auth-context'
import { notifyErrorFor } from '@/utility/notify'
import TwoFactor from '../two-factor'
import styles from './style.module.css'

const schema = yup.object({
  email: yup.string().email('Informe um e-mail válido').required('Preencha o e-mail'),
  password: yup.string().required('Preencha a senha'),
})

type LoginForm = yup.InferType<typeof schema>

const Login = () => {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [pendente2FA, setPendente2FA] = useState<LoginResponse | null>(null)

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<LoginForm>({
    resolver: yupResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  const concluirLogin = (login: LoginAutenticado) => {
    signIn(login)
    void navigate('/autorizacoes', { replace: true })
  }

  const onSubmit = async (data: LoginForm) => {
    try {
      const login = await AuthApi.login(data)
      if (!login) {
        notifyErrorFor('entrar')
        return
      }

      if (login.requer2FA && login.desafio2FA) {
        setPendente2FA(login)
        return
      }

      if (loginAutenticado(login)) {
        concluirLogin(login)
        return
      }

      notifyErrorFor('entrar')
    } catch (error) {
      notifyErrorFor('entrar', error)
    }
  }

  if (pendente2FA?.desafio2FA) {
    return (
      <TwoFactor
        desafio={pendente2FA.desafio2FA}
        email={pendente2FA.userToken.email}
        onCancel={() => setPendente2FA(null)}
        onVerified={concluirLogin}
      />
    )
  }

  return (
    <AuthLayout
      title="Admin IntegraDoor"
      subtitle="Entre com sua conta do backoffice"
      footer={<Link to="/esqueceu-senha">Esqueceu a senha?</Link>}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField label="E-mail" htmlFor="email" required error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Preencha o e-mail"
            iconLeft={<Mail size={16} />}
            invalid={Boolean(errors.email)}
            {...register('email')}
          />
        </FormField>

        <FormField label="Senha" htmlFor="password" required error={errors.password?.message}>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            placeholder="Preencha a senha"
            invalid={Boolean(errors.password)}
            {...register('password')}
          />
        </FormField>

        <Button type="submit" size="lg" block isLoading={isSubmitting}>
          Entrar
        </Button>
      </form>
    </AuthLayout>
  )
}

export default Login
